<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges"></alert-unsaved-changes>
    <v-progress-linear
      v-show="loading || providerLoading"
      color="neutral400"
      indeterminate
      rounded
      height="3"
    ></v-progress-linear>
    <v-form
      v-show="!loading && !providerLoading"
      ref="form"
      v-model="valid"
      :lazy-validation="false"
    >
      <v-row>
        <v-col cols="12" sm="12" md="12">
          <label>{{ $t('description') }}</label>
          <p class="label-info">{{ $t('description-message') }}</p>
          <v-textarea
            v-model="provider.description"
            outlined
            clearable
            :rules="minMaxLengthRule(1, 1000)"
            class="required"
            :disabled="!edit"
            data-cy="description"
            :placeholder="$t('description')"
          ></v-textarea>
          <p class="text-bold neural600--text">
            {{ $t('max-length', { length: '1000' }) }}
          </p>
        </v-col>
        <v-col cols="12" v-if="!next">
          <v-expansion-panels>
            <v-expansion-panel :key="1">
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pt-10 pb-7">
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('logo') }}</label>
                    <p class="label-info">{{ $t('logo-message') }}</p>
                    <v-text-field
                      v-model="provider.logoUrl"
                      outlined
                      clearable
                      :disabled="!edit"
                      data-cy="logoUrl"
                      :rules="checkURL()"
                      :placeholder="$t('logo')"
                      prepend-inner-icon="mdi-link-variant"
                      v-on:keydown.enter.prevent
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <label>{{ $t('social-media') }}</label>
                    <p class="label-info">{{ $t('social-media-message') }}</p>
                    <v-row v-for="(socialMedia, i) in provider.socialMedia" :key="i">
                      <v-col cols="5" class="pb-0">
                        <v-text-field
                          v-model="socialMedia.url"
                          :placeholder="$t('paste-url')"
                          outlined
                          :rules="requiredRule() && checkURL()"
                          class="required"
                          :disabled="!edit"
                          data-cy="social-url"
                          clearable
                          prepend-inner-icon="mdi-link-variant"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="5" class="pb-0">
                        <v-select
                          :disabled="!edit"
                          v-model="socialMedia.type"
                          :items="[
                            'Twitter',
                            'Facebook',
                            'Instagram',
                            'LinkedIn',
                            'Youtube',
                            'Other',
                          ]"
                          outlined
                        ></v-select>
                      </v-col>
                      <v-col cols="2">
                        <v-btn fab small text @click="removeSocialLink(i)">
                          <v-icon>
                            $vuetify.icons.trash
                          </v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-row v-if="edit">
                      <v-col cols="12">
                        <v-btn tile text @click="addSocialLink()">
                          <v-icon left>
                            $vuetify.icons.plus
                          </v-icon>
                          <span
                            class="text-decoration-underline text-transform-none font-weight-bold"
                          >
                            {{ $t('add-social-link') }}
                          </span>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12" v-if="false">
                    <v-row>
                      <v-col cols="5" class="pb-0">
                        <label>{{ $t('multimedia') }}</label>
                        <p class="label-info">{{ $t('multimedia-message') }}</p>
                      </v-col>
                      <v-col cols="7" class="pb-0">
                        <label>{{ $t('multimedia-name') }}</label>
                        <p class="label-info">{{ $t('multimedia-name-message') }}</p>
                      </v-col>
                    </v-row>
                    <v-row v-for="(multimedia, i) in provider.multimedias" :key="i">
                      <v-col cols="5" class="pb-0">
                        <v-text-field
                          v-model="multimedia.url"
                          :placeholder="'Paste a Url'"
                          outlined
                          :rules="requiredRule() && checkURL()"
                          class="required"
                          :disabled="!edit"
                          clearable
                          data-cy="multimedia-url"
                          prepend-inner-icon="mdi-link-variant"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="5" class="pb-0">
                        <v-text-field
                          v-model="multimedia.name"
                          :placeholder="$t('multimedia-name')"
                          outlined
                          :rules="requiredRule()"
                          class="required"
                          :disabled="!edit"
                          clearable
                          data-cy="multimedia-url"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="2">
                        <v-btn fab small text @click="removeMultimediaLink(i)">
                          <v-icon>
                            $vuetify.icons.trash
                          </v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-row class="pt-0" v-if="edit">
                      <v-col cols="12">
                        <v-btn tile text @click="addMultimediaLink()">
                          <v-icon left>
                            $vuetify.icons.plus
                          </v-icon>
                          <span
                            class="text-decoration-underline text-transform-none font-weight-bold"
                          >
                            {{ $t('add-multimedia-link') }}
                          </span>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-form>
    <!--BUTTONS-->
    <v-row class="mt-10" v-if="next">
      <v-col cols="6" class="d-flex justify-end">
        <v-btn
          depressed
          rounded
          x-large
          color="black"
          @click="back()"
          outlined
          class="black--text font-weight-bold"
          data-cy="submit"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
      </v-col>
      <v-col cols="6">
        <v-btn
          depressed
          rounded
          x-large
          color="primary200"
          @click="add()"
          :disabled="!valid"
          class="white--text font-weight-bold"
          data-cy="submit"
          >{{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="mt-10" v-if="edit && !next">
      <v-col cols="6" class="d-flex justify-end" v-if="!admin">
        <v-btn
          depressed
          rounded
          x-large
          color="black"
          @click="cancel()"
          outlined
          class="black--text font-weight-bold"
          data-cy="submit"
        >
          {{ $t('cancel') }}
        </v-btn>
      </v-col>
      <v-col :cols="admin ? '12' : '6'" :class="admin ? 'text-center' : ''">
        <v-btn
          depressed
          rounded
          x-large
          color="neutral"
          @click="add()"
          :disabled="!valid"
          class="white--text font-weight-bold"
          data-cy="submit"
          >{{ $t('update') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';

export default {
  name: 'ProviderMarketingInfo',
  mixins: [util],
  props: {
    admin: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Boolean,
      default: true,
    },
    next: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    AlertUnsavedChanges,
  },
  data() {
    return {
      valid: false,
      loadingComponents: -1,
      unsavedChanges: false,
      provider: {
        id: null,
        description: null,
        logoUrl: null,
        socialMedia: [],
        multimedias: [],
      },
      providerLoading: true,
    };
  },
  created() {
    this.loadingComponents++;
  },
  methods: {
    cancel() {
      this.$emit('cancel', true);
    },
    back() {
      console.log('back!');
      this.$emit('tab', 0);
    },
    add() {
      EventBus.$emit('waiting', true);
      this.axios
        .post('/api/v1/provider-settings/marketing-info', this.provider)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('provider-updated'),
          });
          this.unsavedChanges = false;
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
          this.$emit('tab', 2);
        });
    },
    addSocialLink() {
      this.provider.socialMedia.push({ url: null, type: 'Twitter' });
    },
    removeSocialLink(index) {
      this.provider.socialMedia.splice(index, 1);
    },
    removeMultimediaLink(index) {
      this.provider.multimedias.splice(index, 1);
    },
    addMultimediaLink() {
      this.provider.multimedias.push({ url: null, name: null });
    },
    getProvider(id) {
      EventBus.$emit('waiting', true);
      this.axios
        .get('/api/v1/provider-settings/' + id)
        .then(res => {
          this.provider = res.data;
          this.$nextTick(() => {
            this.providerLoading = false;
          });
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
  },
  computed: {
    loading() {
      return this.loadingComponents !== 0;
    },
  },
  watch: {
    loading(value) {
      let id = this.$route.params.id;
      if (value === false) {
        console.log('Loading');
        if (sessionStorage.getItem('providerId')) {
          this.getProvider(sessionStorage.getItem('providerId'));
        } else if (id != null) {
          this.getProvider(id);
        } else {
          this.$nextTick(() => {
            this.providerLoading = false;
          });
        }
      }
    },
    provider: {
      handler: function(val, oldVal) {
        console.log('[Provider] Value changed');
        if (oldVal.id) {
          this.unsavedChanges = true;
        }
      },
      deep: true,
    },
  },
};
</script>
